import { vars } from '@/app/config/variables';

export class ProductPreview {
	private triggerBlock: HTMLElement;
	private previewBlock: HTMLElement;
	private mainAddToCartWrapper: HTMLElement;
	private header: HTMLElement;
	private headerHeight: number;
	private observer: IntersectionObserver;
	private resizeObserver: ResizeObserver;

	private lastScrollY: number = 0;
	private scrollDirection: 'up' | 'down' = 'down';
	private canReactToScroll: boolean = false;
	private laptopMQ: MediaQueryList;
	private activeClass: string = 'product-preview--active';
	private mainAddToCartWrapperHeight: number = 0;

	constructor(triggerSelector: string, previewSelector: string) {
		const trigger = document.querySelector(triggerSelector);
		const preview = document.querySelector(previewSelector);
		const header = document.querySelector('.header');
		
		if (!trigger || !preview || !header) return;

		this.triggerBlock = trigger as HTMLElement;
		this.previewBlock = preview as HTMLElement;
		this.header = header as HTMLElement;
		this.mainAddToCartWrapper = trigger.querySelector('.js-add-to-cart');

		this.laptopMQ = window.matchMedia(`(min-width: ${vars.breakpoint.laptop}px)`);

		this.calcHeaderHeight();
		this.createIntersectionObserver();
		this.initResizeObserver();
		window.addEventListener('scroll', this.handleScroll, { passive: true });

		if (this.mainAddToCartWrapper) {
			this.mainAddToCartWrapperHeight = this.mainAddToCartWrapper.offsetHeight;
			this.initMutationObserver();
		}
	}

	private createIntersectionObserver(): void {
		this.observer = new IntersectionObserver(this.handleIntersect, {
			root: null,
			threshold: 0,
			rootMargin: `-${this.headerHeight}px 0px 0px 0px`,
		});

		this.observer.observe(this.triggerBlock);
	}

	private resetIntersectionObserver(): void {
		this.observer.disconnect();
		this.createIntersectionObserver();
	}

	private handleIntersect = (entries: IntersectionObserverEntry[]): void => {
		for (const entry of entries) {
			if (entry.isIntersecting) {
				this.previewBlock.classList.remove(this.activeClass);
				this.canReactToScroll = false;
			} else {
				this.canReactToScroll = true;
				if (this.laptopMQ.matches) {
					this.previewBlock.classList.add(this.activeClass);
				}
			}
		}
	};

	private initResizeObserver(): void {
		this.resizeObserver = new ResizeObserver(() => {
			const newHeight = this.header.offsetHeight;
			if (newHeight !== this.headerHeight) {
				this.headerHeight = newHeight;
				this.resetIntersectionObserver();
			}
		});

		this.resizeObserver.observe(this.header);
	}

	private calcHeaderHeight(): void {
		this.headerHeight = this.header.offsetHeight;
	}

	private handleScroll = (): void => {
		if (this.laptopMQ.matches || !this.canReactToScroll) return;

		const offsetY = window.scrollY;
		const triggerTop = this.triggerBlock.getBoundingClientRect().top + window.scrollY;

		if (offsetY < triggerTop) {
			this.previewBlock.classList.remove(this.activeClass);
			this.lastScrollY = offsetY;
			return;
		}

		const isScrollingDown = offsetY > this.lastScrollY;
		const isScrollingUp = offsetY < this.lastScrollY;

		if (isScrollingDown) this.scrollDirection = 'down';
		else if (isScrollingUp) this.scrollDirection = 'up';

		this.lastScrollY = offsetY;

		if (this.scrollDirection === 'up') {
			this.previewBlock.classList.add(this.activeClass);
		} else if (this.scrollDirection === 'down') {
			this.previewBlock.classList.remove(this.activeClass);
		}
	};

	private initMutationObserver(): void {
		const mutationObserver = new MutationObserver(this.handleMutation);
		mutationObserver.observe(this.mainAddToCartWrapper, {
			attributes: true,
			attributeFilter: ['class'],
		});
	}

	private handleMutation = (mutations: MutationRecord[]): void => {
		mutations.forEach(mutation => {
			if (mutation.attributeName !== 'class') return;

			window.removeEventListener('scroll', this.handleScroll);

			const targetHeight = (mutation.target as HTMLElement).offsetHeight;

			if (targetHeight !== this.mainAddToCartWrapperHeight) {
				this.mainAddToCartWrapperHeight = targetHeight - this.mainAddToCartWrapperHeight;
				this.lastScrollY += this.mainAddToCartWrapperHeight;					
			}

			window.addEventListener('scroll', this.handleScroll, { passive: true });			
			
		});
	};
	
}
